import React, { useState, useMemo, useRef } from 'react'
import './ImageViewerListGridStyle.css'
import { Dropdown } from 'react-bootstrap'
import dicom_icon from '@assets/images/dicom_icon.svg'
import ImageCard from '../ImageCard/ImageCard.component'
import { PagerComponent, PageEventArgs } from '@syncfusion/ej2-react-grids'
import { formatBytesToMB } from '@utils/imageViewer.utils'
import { IFolder, IFile } from '@dataTypes/folder.types'
import { ISelectedFile } from '../ImageViewerList/hooks/useImageViewerState'

import { useDispatch } from 'react-redux'
import { AppDispatch } from '@store/index'
import { useAppSelector } from '@store/hooks'
import {
  deleteFileAction,
  toggleFileFavoriteAction,
  downloadFileAction,
  uploadFileAction,
  fetchFolderById,
} from '@store/actions/folder.action'
import { logger } from '@utils/safeLogger'
type ImageViewerListGridProps = {
  selectedFiles: ISelectedFile[]
  setSelectedFiles: React.Dispatch<React.SetStateAction<ISelectedFile[]>>
  searchTerm: string
  favoritesOnly: boolean
  files: IFile[]
  loading: boolean
  selectedFolder: IFolder | null
}

function ImageViewerListGrid({
  selectedFiles,
  setSelectedFiles,
  searchTerm,
  favoritesOnly,
  files,
  loading,
  selectedFolder,
}: ImageViewerListGridProps): React.ReactElement {
  const dispatch: AppDispatch = useDispatch()
  const { selectedPatient } = useAppSelector((state) => state.patient)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  const [currentPageLocal, setCurrentPageLocal] = useState<number>(1)
  const [pageSizeLocal, setPageSizeLocal] = useState<number>(10)

  // Filter files based on search term and favorites
  const filteredFiles = useMemo(() => {
    // Ensure files is always an array
    if (!files || !Array.isArray(files)) {
      return []
    }
    let filtered = files

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (file) =>
          file.file_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          file.description?.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    }

    // Apply favorites filter
    if (favoritesOnly) {
      filtered = filtered.filter((file) => file.is_favorite)
    }

    return filtered
  }, [files, searchTerm, favoritesOnly])

  // Calculate total count for pagination
  const total = filteredFiles.length

  // Get paginated files
  const paginatedFiles = useMemo(() => {
    const startIndex = (currentPageLocal - 1) * pageSizeLocal
    const endIndex = startIndex + pageSizeLocal
    return filteredFiles.slice(startIndex, endIndex)
  }, [filteredFiles, currentPageLocal, pageSizeLocal])

  const handleFavorite = async (fileId: string): Promise<void> => {
    try {
      const file = files.find((f) => f.file_id === fileId)
      if (file) {
        await dispatch(
          toggleFileFavoriteAction({
            fileId,
            isFavorite: !file.is_favorite,
          }),
        ).unwrap()
      }
    } catch (error) {
      logger.error('Failed to toggle favorite:', error)
    }
  }

  const handleDelete = async (fileId: string): Promise<void> => {
    try {
      await dispatch(deleteFileAction(fileId)).unwrap()
      // Remove from selected files if it was selected
      setSelectedFiles((prev) => prev.filter((file) => file.file_id !== fileId))
    } catch (error) {
      logger.error('Failed to delete file:', error)
    }
  }

  const handleDownload = async (fileId: string): Promise<void> => {
    if (!fileId) return
    try {
      const file = files.find((f) => f.file_id === fileId)
      if (file) {
        await dispatch(downloadFileAction(file)).unwrap()
      }
    } catch (error) {
      logger.error('Failed to download file:', error)
    }
  }

  const onPageChange = (args: PageEventArgs): void => {
    const newPage = parseInt(args.currentPage as string, 10) || 1
    setCurrentPageLocal(newPage)
  }

  const handlePageSizeChange = (newPageSize: number): void => {
    setPageSizeLocal(newPageSize)
    setCurrentPageLocal(1) // Reset to first page when changing page size
  }

  const handleSelect = (fileId: string): void => {
    if (!fileId) return

    const file = files.find((f) => f.file_id === fileId)
    if (!file) return

    setSelectedFiles((prev) => {
      const isSelected = prev.some((selected) => selected.file_id === fileId)
      if (isSelected) {
        return prev.filter((selected) => selected.file_id !== fileId)
      } else {
        return [
          ...prev,
          {
            file_id: fileId,
            study_uid: file.study_uid,
            series_uid: file.series_uid,
          },
        ]
      }
    })
  }

  const pageSizeOptions = [10, 25, 50, 100]

  // Handle file upload
  const handleFileUpload = async (
    uploadedFiles: FileList | File[],
  ): Promise<void> => {
    if (!selectedFolder || !selectedPatient) {
      return
    }

    const filesArray = Array.from(uploadedFiles)

    try {
      // Upload all files
      await Promise.all(
        filesArray.map((file) =>
          dispatch(
            uploadFileAction({
              file,
              patientId: selectedPatient.patient_id,
              folderId: selectedFolder.folder_id,
            }),
          ).unwrap(),
        ),
      )

      // Refresh folder to show new files
      dispatch(fetchFolderById(selectedFolder.folder_id))
    } catch (error) {
      logger.error('Failed to upload file:', error)
    }
  }

  const handleFileInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileUpload(e.target.files)
      // Reset input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files)
    }
  }

  const handleEmptyStateClick = (): void => {
    fileInputRef.current?.click()
  }

  // Show loading state
  if (loading) {
    return (
      <div className="image-viewer-list-grid">
        <div className="loading-container"></div>
      </div>
    )
  }

  // Show no studies message
  if (!selectedFolder) {
    return (
      <div className="image-viewer-list-grid">
        <div className="no-folder-selected"></div>
      </div>
    )
  }

  // Show empty state when folder has no files
  if (!files || !Array.isArray(files) || files.length === 0) {
    return (
      <div className="image-viewer-list-grid">
        <div
          className={`empty-folder-state ${isDragging ? 'drag-over' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleEmptyStateClick}
          style={{ cursor: 'pointer' }}
        >
          <div className="empty-folder-icon">
            <i className="mgc_file_upload_fill"></i>
          </div>
          <div className="empty-folder-text">
            <div className="empty-folder-title">
              Drag and drop or{' '}
              <span style={{ color: '#185DD8', textDecoration: 'none' }}>
                choose file
              </span>
            </div>
            <div className="empty-folder-subtitle">No files to show here</div>
          </div>
          <input
            type="file"
            ref={fileInputRef}
            className="d-none"
            onChange={handleFileInputChange}
            multiple
          />
        </div>
      </div>
    )
  }

  return (
    <div className="image-viewer-list-grid">
      <div className="image-grid-container">
        {paginatedFiles.map((file, i) => (
          <ImageCard
            key={file.file_id || i}
            imageUrl={dicom_icon}
            fileName={file.file_name || file.original_name}
            fileSize={
              file?.file_size ? `${formatBytesToMB(file?.file_size)} MB` : ''
            }
            uploadDate={
              file.date_time_created_at
                ? new Date(file.date_time_created_at).toLocaleString()
                : ''
            }
            isSelected={
              file.file_id
                ? selectedFiles.some(
                    (selected) => selected.file_id === file.file_id,
                  )
                : false
            }
            isFavorite={file.is_favorite || false}
            onSelect={() => handleSelect(file.file_id)}
            onFavorite={() => handleFavorite(file.file_id || '')}
            onDelete={() => handleDelete(file.file_id || '')}
            onDownload={() => handleDownload(file.file_id || '')}
          />
        ))}
      </div>

      <div className="pagination-container">
        <Dropdown className="">
          <Dropdown.Toggle className="viewport-header-dropdown">
            {pageSizeLocal} results/page
          </Dropdown.Toggle>

          <Dropdown.Menu>
            {pageSizeOptions.map((size) => (
              <Dropdown.Item
                key={size}
                onClick={() => handlePageSizeChange(size)}
                active={pageSizeLocal === size}
              >
                {size} results/page
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
        <PagerComponent
          totalRecordsCount={total}
          pageSize={pageSizeLocal}
          currentPage={currentPageLocal}
          click={onPageChange}
        />
      </div>
    </div>
  )
}

export default ImageViewerListGrid
